// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import RadioGroup from "@insite/mobius/RadioGroup/RadioGroup";
import Radio from "@insite/mobius/Radio/Radio";
import DatePicker from "@insite/mobius/DatePicker/DatePicker";
import TextArea from "@insite/mobius/TextArea/TextArea";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import React, { useState } from "react";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import {
    RequestTrailerPickupResponseModel,
    TrailerModelV2,
    TrailerPickupRequestModel,
} from "@store/Framework/Types/ApiModels";
import styled, { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { DatePickerState } from "@store/MobiusOverrides/DatePicker/DatePicker";
import getColor from "@insite/mobius/utilities/getColor";
import GridContainer, { GridContainerProps } from "@store/MobiusOverrides/GridContainer/GridContainer";
import GridItem, { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import siteMessage from "@insite/client-framework/SiteMessage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import FormActions, { FormActionsProps } from "@store/Library/Components/Toolkit/FormActions";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import requestTrailerPickup from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/Handlers/requestTrailerPickup";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";

type PickupRequestItem = {
    shipToNumber: string;
    shipToAddress: string;
    trailer: TrailerModelV2;
};

export interface TrailerTrackingPickupRequestModalProps extends ExpandStyles<TrailerTrackingPickupRequestModalStyles> {
    isOpen: boolean;
    onCancel: () => void;
    onSubmitPickupRequest: (result: { success: boolean; trailerPickupRequest: TrailerPickupRequestModel }) => void;
    pickupRequestItem: PickupRequestItem | undefined;
}

export interface TrailerTrackingPickupRequestModalStyles {
    modal: ModalPresentationProps;
    modalTitle?: TypographyPresentationProps;
    headerItemSubTitle?: TypographyPresentationProps;
    headerItemText?: TypographyPresentationProps;
    headerWrapper?: GridContainerProps;
    headerGridItem?: GridItemProps;
    pickupOptionInfo?: TypographyPresentationProps;
    actionsWrapper?: GridContainerProps;
    actionButton?: ButtonPresentationProps;
    requestedDateWrapper?: InjectableCss;
    formActions?: FormActionsProps;
}
type PickupOptions = "next-delivery" | "bobtail";

export const trailerTrackingPickupRequestModalStyles: TrailerTrackingPickupRequestModalStyles = {
    modal: {
        sizeVariant: "small",
    },
    modalTitle: {
        variant: "p",
        css: css`
            text-align: left;
            margin-bottom: ${pxToRem(24)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            font-size: ${pxToRem(20)};
        `,
    },
    headerWrapper: {},
    headerItemSubTitle: {
        variant: "p",
        css: css`
            margin-bottom: ${pxToRem(8)};
            color: ${getColor("text.light")};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    headerItemText: {
        variant: "p",
    },
    headerGridItem: {
        css: css`
            flex-direction: column;
        `,
    },
    pickupOptionInfo: {
        variant: "p",
        css: css`
            color: ${getColor("text.light")};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    actionsWrapper: {
        gap: 16,
        css: css`
            margin-top: ${pxToRem(24)};
        `,
    },
    requestedDateWrapper: {
        css: css`
            margin-top: ${pxToRem(10)};
        `,
    },
    formActions: {
        spacing: pxToRem(16),
        extendedStyles: {
            flexWrapper: {
                content: {
                    css: css`
                        ${breakpointValues(["column", "row", null, null, null], "flex-direction")}
                    `,
                },
                item: {
                    css: css`
                        flex: 1;
                    `,
                },
            },
        },
    },
};

const Separator = styled.hr`
    border: 0;
    border-top: ${pxToRem(1)} solid ${getColor("common.borderLight")};
    margin: ${pxToRem(16)} 0;
`;

const TrailerTrackingPickupRequestModal = ({
    extendedStyles,
    overridedStyles,
    isOpen,
    onCancel,
    onSubmitPickupRequest,
    pickupRequestItem,
}: TrailerTrackingPickupRequestModalProps) => {
    const styles = useExpandProps(trailerTrackingPickupRequestModalStyles, extendedStyles, overridedStyles);

    const dispatch = useAppDispatch();
    const [pickupOption, setPickupOption] = useState<PickupOptions>("next-delivery");
    const [additionalNotes, setAdditionalNotes] = useState("");
    const [requestedPickupDate, setRequestedPickupDate] = useState<Date | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);

    const resetForm = () => {
        setPickupOption("next-delivery");
        setAdditionalNotes("");
        setRequestedPickupDate(undefined);
    };

    const onSubmitPickupRequestSuccess = (result: RequestTrailerPickupResponseModel) => {
        setIsSubmitting(false);
        resetForm();
        onSubmitPickupRequest({ success: true, trailerPickupRequest: result.dropTrailerPickupRequest });
    };

    const onSubmitPickupRequestFailure = (_result: string) => {
        setIsSubmitting(false);
        setHasSubmitError(true);
    };

    const handleOnSubmit = () => {
        if (!pickupRequestItem) {
            return;
        }

        setIsSubmitting(true);
        dispatch(
            requestTrailerPickup({
                trailerId: pickupRequestItem.trailer.id,
                shipmentNumber: pickupRequestItem.trailer.shipmentNumber,
                shipToNumber: pickupRequestItem.shipToNumber,
                pickupOption,
                requestedPickupDate: pickupOption === "bobtail" ? requestedPickupDate : undefined,
                additionalNotes,
                onSuccess: onSubmitPickupRequestSuccess,
                onError: onSubmitPickupRequestFailure,
            }),
        );
    };

    const handleCancel = () => {
        onCancel();
        resetForm();
    };

    const handleRequestedPickupDateChange = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setRequestedPickupDate(selectedDay);
    };

    if (!pickupRequestItem) {
        return null;
    }

    return (
        <Modal
            {...styles.modal}
            hideTitle={true}
            isOpen={isOpen}
            isCloseable={!isSubmitting}
            handleClose={handleCancel}
            data-test-selector={"TrailerTracking_PickupRequestModal"}
        >
            <Typography {...styles.modalTitle}>{translate("Trailer Pickup Request")}</Typography>
            {hasSubmitError && <InfosBox type={"error"}>{translate("An error occurred. Try again later.")}</InfosBox>}

            <LoadingOverlay loading={isSubmitting}>
                <StyledWrapper>
                    <GridContainer {...styles.headerWrapper}>
                        <GridItem {...styles.headerGridItem} width={8}>
                            <Typography {...styles.headerItemSubTitle}>{translate("Pickup Location")}</Typography>
                            <Typography
                                {...styles.headerItemText}
                                data-test-selector={"TrailerTracking_PickupRequestModal_ShipToAddress"}
                            >
                                {pickupRequestItem.shipToAddress}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.headerGridItem} width={4}>
                            <Typography {...styles.headerItemSubTitle}>{translate("Trailer ID")}</Typography>
                            <Typography
                                {...styles.headerItemText}
                                data-test-selector={"TrailerTracking_PickupRequestModal_TrailerId"}
                            >
                                {pickupRequestItem.trailer.id}
                            </Typography>
                        </GridItem>
                    </GridContainer>

                    <Separator />

                    <RadioGroup
                        label={translate("Select Pickup Option")}
                        value={pickupOption}
                        onChangeHandler={event => {
                            setPickupOption(event.target.value as PickupOptions);
                        }}
                        data-test-selector={"TrailerTracking_PickupRequestModal_PickupOptions"}
                    >
                        <Radio value="next-delivery">{translate("Pick up with next delivery")}</Radio>
                        <Radio value="bobtail">
                            <StyledWrapper>
                                <Typography>{translate("Request bobtail pickup")}</Typography>
                                <Typography {...styles.pickupOptionInfo}>
                                    {siteMessage("DropTrailer_PickupRequest_PickupOption_Bobtail_Info")}
                                </Typography>
                            </StyledWrapper>
                        </Radio>
                    </RadioGroup>

                    {pickupOption === "bobtail" && (
                        <StyledWrapper {...styles.requestedDateWrapper}>
                            <DatePicker
                                label={translate("Requested Date for Pickup")}
                                selectedDay={requestedPickupDate}
                                onDayChange={handleRequestedPickupDateChange}
                                dateTimePickerProps={{
                                    minDate: new Date(),
                                }}
                                data-test-selector={"TrailerTracking_PickupRequestModal_RequestedPickupDate"}
                            />
                        </StyledWrapper>
                    )}

                    <Separator />

                    <TextArea
                        label={translate("Additional Notes")}
                        value={additionalNotes}
                        rows={5}
                        placeholder={translate("Add notes, questions or concerns here")}
                        onChange={event => setAdditionalNotes(event.currentTarget.value)}
                        data-test-selector={"TrailerTracking_PickupRequestModal_AdditionalNotes"}
                    />

                    <FormActions {...styles.formActions}>
                        <Button
                            {...styles.actionButton}
                            onClick={handleOnSubmit}
                            data-test-selector={"TrailerTracking_PickupRequestModal_SubmitButton"}
                        >
                            {translate("Send pickup request")}
                        </Button>

                        <Button
                            {...styles.actionButton}
                            variant="tertiary"
                            onClick={handleCancel}
                            data-test-selector={"TrailerTracking_PickupRequestModal_CancelButton"}
                        >
                            {translate("Cancel")}
                        </Button>
                    </FormActions>
                </StyledWrapper>
            </LoadingOverlay>
        </Modal>
    );
};

export default TrailerTrackingPickupRequestModal;
